import { Directive, EventEmitter, Input } from "@angular/core";
import { INeo4j } from "../interfaces/Neo4j";
import { Connection } from "../clases/Connection";
import { AbstractShape } from "./Shape";
import { IView } from "../interfaces/View";
import { ViewFromServer } from "../interfaces/ViewFromServer";
import { ViewsCanvas } from "../clases/ViewsCanvas";
import { BeNeo4jService } from "../services/be-neo4j.service";
import { SharedService } from "../services/shared.service";
import { getAllDatabaseInfo } from "../utils/RequestUtils";
import { ISchemaTree } from "../interfaces/SchemaTree";
import { createSchemaTreeRecursive, generateUUID } from "../utils/utils";
import { MainComponent } from "../pages/main/main.component";
import { createCanvasItem, createIContainers, getAllShapesWithLabels, getAllShapesWithPropierty, getShapeWithNodeId, makeContainers, removeChildItemsFromMainList } from "../utils/ContainerUtils";
import { createIConnections, makeConnections, recreateConnection } from "../utils/ConnectionUtils";
import { DBItem, Record } from "../interfaces/Record";
import { hierarchyLoop, hierarchyLoopSimple, obtainHierachy } from "../utils/HierarchyUtils";
import { assignIncomingNodes, extractElements, separateNodesFromRelations } from "../utils/RecordUtils";
import { forkJoin, map as rxMap, Observable, of } from "rxjs";
import { Importer } from "../clases/Importer";
import { fruchtermanReingold, resizeBoxes } from "../utils/PositionUtils";
import { Values } from "../clases/Values";
import { ShapeBounding } from "../clases/ShapeBounding";
import { CanvasColorTheme } from "../interfaces/CanvasColorTheme";
import { Shape } from "../enums/Shape";
import { ShapeRefArray } from "../clases/ShapeRefArray";
import { ConnectionRef } from "../clases/ConnectionRef";
import { clearConnectionFocus, clearFocus } from "../utils/ListUtils";
import { INeo4jData } from "../interfaces/INeo4jData";
import { ServerResponse, ServerResponseObject } from "../interfaces/ServerResponse";
import { ShapeRef } from "../clases/ShapeRef";
import { Exporter } from "../clases/Exporter";
import { AllMetricsTypes } from "../interfaces/MetricType";
import { TabType } from "../enums/TabType";

import { LiveCanvasState } from '../agent/live-canvas-state';
import { canvasSnapshot } from '../agent/live-canvas-snapshot';

interface PendingViewCypher {
  view: IView;
  nodeId: string;
  hierarchy: string;
}

@Directive()
export abstract class DataViewer {
  /** Center and fit the current Canvas using the renderer's native camera. */
  abstract fitBoxesWithCamera: () => void;

  @Input() agentTabId = '';
  agentContextRevision = 0;
  agentCanvasState = new LiveCanvasState();
  agentGestureActive = false;
  agentApplying = false;

  observeAgentCanvas(): void {
    if (this.agentGestureActive || this.agentApplying) return;
    this.agentCanvasState.observe(canvasSnapshot(this.canvasItems, this.connections), this.isLoading ? 'load' : 'user');
  }

  constructor(
    protected beNeo4jService: BeNeo4jService,
    protected sharedService: SharedService
  ) { }

  protected _appComponent!: MainComponent;

  get appComponent(): MainComponent {
    return this._appComponent;
  }

  set appComponent(value: MainComponent) {
    this._appComponent = value;
  }

  protected _canvasBounding!: ShapeBounding;

  get canvasBounding(): ShapeBounding {
    return this._canvasBounding;
  }

  set canvasBounding(value: ShapeBounding) {
    this._canvasBounding = value;
  }

  canvasItems: AbstractShape[] = [];
  connections: Connection[] = [];

  savedDocument!: INeo4j;
  importDocument!: INeo4j;
  viewCanvas: ViewsCanvas[] = [];
  _isViewEditable: boolean = true;

  currentFocus: ShapeRefArray = new ShapeRefArray(this);
  currentConnectionFocus: ConnectionRef = new ConnectionRef();
  hoveredConnection: ConnectionRef = new ConnectionRef();
  hoveredShape: ShapeRef = new ShapeRef();
  viewFromServer!: ViewFromServer;

  dataReady: EventEmitter<any> = new EventEmitter();

  private generatedCanvasViewUUID: string = generateUUID();

  isLoading: boolean = false;

  canvasSize: Values = new Values(5000, 5000);

  minBoxSize: number = 100;

  showMetricsButton: boolean = false;
  protected readonly TabType = TabType;

  getMetricTypes() {
    this.beNeo4jService.MetricsTypes(this.savedDocument.hierarchy, this.savedDocument.connection.dataService.data_service_id).subscribe({
      next: (response: ServerResponseObject<AllMetricsTypes>) => {
        let metrics = response.result;
        if(Object.keys(metrics).length){
          this.showMetricsButton = true;
          return;
        }
        this.showMetricsButton = false;
      },
      error: (err) => {
        this.isLoading = false;
      }
    });
  }

  onNewView = (importer: Importer, callback?: Function)=>{
    this.agentContextRevision++;
    this.agentCanvasState = new LiveCanvasState();
    // Avoid drawing before a brand-new tab has initialized its Canvas.
    // A reused tab can still have selected shapes that must be released.
    if (this.currentFocus.getShape().length) this.currentFocus.resetList();
    this.currentConnectionFocus.setConnection(null);
    this.hoveredConnection.setConnection(null);
    this.hoveredShape.setShape(null);
    this.canvasItems = [];
    this.connections = [];
    this.viewCanvas = [];
    this.viewFromServer = importer.view!;
    this.savedDocument = importer.connection;
    if (!this.isDocumentComplete(this.savedDocument.connection)) {
      this.restoreDatabase(importer, callback);
    } else {
      if(callback)callback();
      this.appComponent.taxonomyOverview.setSchemaTree(this.savedDocument.connection.schemaTree);
      this.dataReady.emit();
    }
  }

  isDocumentComplete(neo4j_data: INeo4jData) {
    return (
      neo4j_data.DBlabels.length &&
      neo4j_data.DBrelations.length &&
      neo4j_data.hierarchy.length &&
      neo4j_data.schemaTree.length
    );
  }

  /**
   * Reuse the normal View import path.  The optional callback is deliberately
   * invoked only after the imported View groups were added to viewCanvas; it
   * lets a trusted Agent restore batch serialize imports without maintaining a
   * second Canvas state.
   */
  inyectConnectionData(importer: Importer, onViewGroupsLoaded?: (error?: Error) => void) {
    if (!importer) return;
    if (!importer.connection) return;
    // Older saved Views can contain per-group Canvas data without the canonical
    // Model identity that is already present on their persisted View record.
    // Hydrate only missing group fields from that trusted record so every
    // imported group remains visible to representedModels after restoration.
    const importedModelUuid = importer.view?.model_UUID || importer.connection.model_UUID;
    const importedHierarchy = importer.view?.hierarchy || importer.connection.hierarchy;
    for (const container of importer.connection.containers ?? []) {
      if (!container.model_UUID && importedModelUuid) container.model_UUID = importedModelUuid;
      if (!container.hierarchy && importedHierarchy) container.hierarchy = importedHierarchy;
    }
    this.agentContextRevision++;
    this.agentCanvasState = new LiveCanvasState();
    this.isLoading = true;
    if (!this.viewFromServer) this.viewFromServer = importer.view!;
    if (!this.savedDocument) {
      this.savedDocument = importer.connection;
      if (!this.isDocumentComplete(this.savedDocument.connection)) {
        this.restoreDatabase(importer, () => {
          this.runStartSentence(importer.connection, onViewGroupsLoaded);
        });
        return;
      } else {
        this.runStartSentence(importer.connection, onViewGroupsLoaded);
      }
    } else {
      this.savedDocument.containers.push(...importer.connection.containers);
      this.runStartSentence(importer.connection, onViewGroupsLoaded);
    }
  }

  executeFromMenu(cypher: string, onComplete?: (error?: Error) => void) {
    this.agentContextRevision++;
    this.agentCanvasState = new LiveCanvasState();
    this.executeCypher(cypher, this.processExecution, true, onComplete);
  }

  executeCypher(cypher: string, callback: any, hyperflow: boolean = false, onComplete?: (error?: Error) => void) {
    this.isLoading = true;

    this.beNeo4jService.Execute(cypher).subscribe({
      next: async (res: any) => {
        try {
          if (onComplete && (!Array.isArray(res?.result) || res.result.length === 0)) {
            throw new Error('The Model query returned no nodes');
          }
          callback(res, cypher);
          this.isLoading = false;
          this.endStartSentence();
          onComplete?.();
        } catch (error) {
          this.isLoading = false;
          if (onComplete) onComplete(error instanceof Error ? error : new Error('Model loading failed'));
          else throw error;
        }
      },
      error: (error: unknown) => {
        this.isLoading = false;
        if (!onComplete) return;
        if (error instanceof Error) { onComplete(error); return; }
        const response = error && typeof error === 'object' ? error as { [key: string]: any } : undefined;
        const detail = response?.['error']?.['detail'] ?? response?.['detail'];
        const message = (Array.isArray(detail)
          ? detail.map(item => item?.['msg'] ?? item?.['message']).find(Boolean)
          : detail?.['msg'] ?? detail?.['message'] ?? detail)
          ?? response?.['error']?.['message'] ?? response?.['message'];
        onComplete(new Error(typeof message === 'string' && message.trim()
          ? message.replace(/\s+/g, ' ').slice(0, 600)
          : 'Model query failed'));
      },
    });
  }

  processExecution = (res: any, cypher: string) => {
    let canvasList: AbstractShape[] = [];
    let connectionsList: Connection[] = [];
    this.recordToCanvas(res.result, canvasList, connectionsList);
    for (const item of canvasList) {
      resizeBoxes(item, this.minBoxSize);
    }
    fruchtermanReingold(canvasList, connectionsList, this.canvasSize.x, this.canvasSize.y, 100, 500, this.canvasBounding.width, this.canvasBounding.height);
    for (const item of canvasList) this.canvasItems.push(item);
    for (const connection of connectionsList) this.connections.push(connection);
    const canvasIdentity = this.getCypherSourceFromFirstFather(canvasList);
    const canvasView = new ViewsCanvas(
      canvasList,
      connectionsList,
      cypher,
      generateUUID(),
      canvasIdentity?.nodeId,
      canvasIdentity?.hierarchy
    );
    this.viewCanvas.push(canvasView);
    this.updateContainers();
    this.afterFetchingContent();
  }

  processView = (res: any, view: IView) => {
    if(res.result.length === 0) return this.errorReporting("Nodes not Found", "No nodes were found in the response.");
    let canvasList: AbstractShape[] = [];
    let connectionsList: Connection[] = [];
    let containers = view.view;
    let connections = view.connections;
    makeContainers(containers, canvasList);
    let nodes: DBItem[] = [];
    let relations: DBItem[] = [];
    let elements = extractElements(res.result);
    separateNodesFromRelations(elements, nodes, relations);
    let flowRelations = relations.filter((r: DBItem) => r.type === "FLOW");
    assignIncomingNodes(nodes, canvasList, containers);
    if (connections && connections.length) {
      recreateConnection(connections, flowRelations, connectionsList, canvasList);
    } else {
      makeConnections(flowRelations, connectionsList, canvasList);
    }

    this.tryToUseLeftovers(nodes, relations, canvasList);

    for (const item of canvasList) this.canvasItems.push(item);
    for (const connection of connectionsList) this.connections.push(connection);
    this.viewCanvas.push(new ViewsCanvas(canvasList, connectionsList, view.sentence, view.uuid, view.model_UUID, view.hierarchy));
    this.updateContainers();
    this.afterFetchingContent();
  }

  tryToUseLeftovers(nodes: DBItem[], relations: DBItem[], canvasList: AbstractShape[]) {
    let hRelations = relations.filter((rel) => !rel.isUsed && rel.type === "HIERARCHY");
    hierarchyLoop(nodes, hRelations, obtainHierachy(relations, this.savedDocument), canvasList, canvasList, undefined, false);
    
    let filteredRelations = relations.filter((rel) => !rel.isUsed && rel.type === "FLOW");
    let relationsList: Connection[] = [];
    makeConnections(filteredRelations, relationsList, canvasList);

    let notUsedNodes = nodes.filter((node)=>!node.isUsed);
    let addedNodes = notUsedNodes.map((node)=>getShapeWithNodeId(node.elementId, canvasList));
    let notUndefined = addedNodes.filter((item)=>item) as AbstractShape[];
    let filter = removeChildItemsFromMainList(notUndefined);
    for (const item of filter) {
      resizeBoxes(item, this.minBoxSize);
      let canvasSize = new Values(this.canvasSize.x, this.canvasSize.y);
      let canvasBounding = new Values(this.canvasBounding.width, this.canvasBounding.height);
      if(item.parent) {
        canvasSize = new Values(item.parent.width, item.parent.height);
        canvasBounding = new Values(item.parent.width/2, item.parent.height/2);
      }
      fruchtermanReingold(filter, relationsList, canvasSize.x, canvasSize.y, 100, 500, canvasBounding.x, canvasBounding.y);
    }

    for (const connection of relationsList) this.connections.push(connection);
  }

  findHyperFlows = (callback?: any | undefined) => {
    this.isLoading = true;
    this.beNeo4jService.getHyperFlows().subscribe({
      next: (result: any) => {
        if(!result) return;
        if(!result.result) return;
        let nodes: DBItem[] = [];
        let relations: DBItem[] = [];
        let elements = extractElements(result.result);
        separateNodesFromRelations(elements, nodes, relations);
        let hyperFlowRelations = relations.filter((r: DBItem) => r.type === "HYPER_FLOW");
        makeConnections(hyperFlowRelations, this.connections, this.canvasItems);
        this.isLoading = false;
        if (callback) callback();
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  runStartSentence(savedDocument: INeo4j, onViewGroupsLoaded?: (error?: Error) => void) {
    this.isLoading = true;
    let dataservice = this.sharedService.data_services!.find((ds) => ds.data_service_id === savedDocument.connection.dataService.data_service_id);
    if (dataservice) savedDocument.connection.dataService.string_connection = dataservice.string_connection;

    let saved = JSON.parse(JSON.stringify(savedDocument));

    let requests = saved.containers.map((container: IView) => this.beNeo4jService.Execute(container.sentence));
    forkJoin(requests).subscribe({
      next: (responses: any) => {
        for (let index = 0; index < responses.length; index++) {
          const element = responses[index];
          this.processView(element, saved.containers[index]);
        }
        // The complete represented-Model composition is now observable.  Do
        // not wait for decorative redraw/hyperflow work before allowing the
        // next ordered View import to begin.
        onViewGroupsLoaded?.();
        this.endStartSentence();
      },
      error: () => {
        this.isLoading = false;
        onViewGroupsLoaded?.(new Error('View data could not be loaded'));
      }
    });
  }

  restoreDatabase = (importer: Importer, callback?: any) => {
    let dsData = this.sharedService.data_service_data;
    if(this.isDocumentComplete(dsData)){
      let neo4j_data = importer.connection.connection;
      neo4j_data.DBrelations = dsData.DBrelations;
      neo4j_data.DBlabels = dsData.DBlabels;
      neo4j_data.hierarchy = dsData.hierarchy;
      neo4j_data.schemaTree = dsData.schemaTree;
      this.endProcedure(neo4j_data, callback);
      return;
    }

    this.isLoading = true;
    getAllDatabaseInfo(this.beNeo4jService).subscribe({
      // next: ([labels, relationships, hierarchy, schemaTree]) => {
      next: ([labels, relationships, schemaTree]) => {
        this.isLoading = false;
        let sourceTree = (schemaTree as any).result;
        let tree = sourceTree.map((schema: ISchemaTree) => createSchemaTreeRecursive(schema));
        let neo4j_data = importer.connection.connection;
        neo4j_data.DBrelations = (relationships as any).result;
        neo4j_data.DBlabels = (labels as ServerResponse<string>).result;
        // neo4j_data.hierarchy = (hierarchy as any).result as COptionsResponse[];
        neo4j_data.hierarchy = [];
        neo4j_data.schemaTree = tree;
        this.sharedService.data_service_data = neo4j_data;
        this.endProcedure(neo4j_data, callback);
      },
      error: (err) => {
        this.isLoading = false;
      }
    })
  }

  endProcedure = (neo4j_data: INeo4jData, callback: Function)=> {
    this.appComponent.taxonomyOverview.setSchemaTree(neo4j_data.schemaTree);
    this.dataReady.emit();
    if (callback) callback();
  }

  updateCurrentSchemaTree(){
    this.beNeo4jService.schemaTree().subscribe({
      next: (schemaTree)=>{
        let tree = (schemaTree as any).result.map((schema: ISchemaTree) => createSchemaTreeRecursive(schema));
        this.savedDocument.connection.schemaTree = tree;
        this.appComponent.changeSchemaTree(tree);
      }
    });
  }

  recordToCanvas = (records: Record[], list: AbstractShape[], connections: Connection[]) => {
    let nodes: DBItem[] = [];
    let relations: DBItem[] = [];

    let elements = extractElements(records);
    separateNodesFromRelations(elements, nodes, relations);

    let containerRelations = relations.filter((r: DBItem) => r.type === "HIERARCHY");
    let flowRelations = relations.filter((r: DBItem) => r.type === "FLOW");
    // hierarchyLoop(nodes, containerRelations, obtainHierachy(relations, this.savedDocument), list, list);
    hierarchyLoopSimple(nodes, containerRelations, list);

    nodes.filter((n) => !n.isUsed).forEach((item: DBItem) => {
      list.push(createCanvasItem(item));
    });

    makeConnections(flowRelations, connections, list);
    this.updateContainers();
  }

  updateContainers = (): PendingViewCypher[] => {
    if (!this.savedDocument) return [];
    const pendingViewCyphers: PendingViewCypher[] = [];
    const serializedShapeIds = new Set<string>();
    let neo4jData = {
      dataService: this.savedDocument.connection.dataService,
      DBrelations: [],
      DBlabels: [],
      hierarchy: [],
      schemaTree: [],
    } as INeo4jData;
    this.importDocument = {
      containers: [],
      name_value: this.savedDocument.name_value,
      connection: neo4jData,
      model_UUID: this.savedDocument.model_UUID,
      hierarchy: this.savedDocument.hierarchy
    } as INeo4j;
    for (const view of this.viewCanvas) {
      let viewContainers = this.canvasItems.filter(item=>view.view.some(cont=>item.id === cont.id));
      if(!viewContainers.length) continue;
      this.addShapeTreeIds(viewContainers, serializedShapeIds);
      let containers = createIContainers(viewContainers, view.view);

      const cypherSource = this.getCypherSourceFromFirstFather(viewContainers);
      const hierarchy = cypherSource?.hierarchy || this.getViewHierarchy(viewContainers, view);
      let conn = this.getConnectionsForShapes(viewContainers);
      let connections = createIConnections(conn, this.connections);
      let containerView: IView = {
        view: containers,
        connections: connections,
        uuid: view.uuid,
        sentence: view.sentence || "",
        model_UUID: view.model_UUID || cypherSource?.nodeId || this.savedDocument.model_UUID,
        hierarchy
      } as IView;
      if (!containerView.sentence && cypherSource) pendingViewCyphers.push({ view: containerView, ...cypherSource });
      this.importDocument.containers.push(containerView);
    }

    const untrackedRoots = this.canvasItems.filter(item => !serializedShapeIds.has(item.id));
    if (!untrackedRoots.length) {
      this.syncDocumentIdentityFromContainers();
      return pendingViewCyphers;
    }

    const cypherSource = this.getCypherSourceFromFirstFather(untrackedRoots);
    if (!cypherSource) {
      this.syncDocumentIdentityFromContainers();
      return pendingViewCyphers;
    }

    let generatedView: IView = {
      view: createIContainers(untrackedRoots, this.canvasItems),
      connections: createIConnections(this.getConnectionsForShapes(untrackedRoots), this.connections),
      uuid: this.generatedCanvasViewUUID,
      sentence: "",
      model_UUID: cypherSource.nodeId,
      hierarchy: cypherSource.hierarchy
    } as IView;
    pendingViewCyphers.push({ view: generatedView, ...cypherSource });
    this.importDocument.containers.push(generatedView);
    this.syncDocumentIdentityFromContainers();
    return pendingViewCyphers;
  }

  private syncDocumentIdentityFromContainers() {
    const identity = this.getSingleViewIdentity(this.importDocument.containers);

    if (identity.hierarchy) {
      this.importDocument.hierarchy = identity.hierarchy;
      this.savedDocument.hierarchy = identity.hierarchy;
    }

    if (identity.modelUUID) {
      this.importDocument.model_UUID = identity.modelUUID;
      this.savedDocument.model_UUID = identity.modelUUID;
    }
  }

  private getSingleViewIdentity(containers: IView[]) {
    const hierarchies = new Set<string>();
    const modelUUIDs = new Set<string>();

    for (const container of containers) {
      if (container.hierarchy) hierarchies.add(container.hierarchy);
      if (container.model_UUID) modelUUIDs.add(container.model_UUID);
    }

    return {
      hierarchy: hierarchies.size === 1 ? Array.from(hierarchies)[0] : "",
      modelUUID: modelUUIDs.size === 1 ? Array.from(modelUUIDs)[0] : ""
    };
  }

  private addShapeTreeIds(items: AbstractShape[], shapeIds: Set<string>) {
    for (const item of items) {
      shapeIds.add(item.id);
      this.addShapeTreeIds(item.children, shapeIds);
    }
  }

  private getConnectionsForShapes(items: AbstractShape[]) {
    const shapeIds = new Set<string>();
    this.addShapeTreeIds(items, shapeIds);

    return this.connections.filter((connection: Connection) => {
      return shapeIds.has(connection.from) && shapeIds.has(connection.to);
    });
  }

  private getViewHierarchy(items: AbstractShape[], view: ViewsCanvas) {
    return view.hierarchy || this.getFirstHierarchy(items) || this.getFirstHierarchy(view.view) || this.savedDocument.hierarchy || "";
  }

  private getCypherSourceFromFirstFather(items: AbstractShape[]): Omit<PendingViewCypher, "view"> | null {
    const fatherNode = this.getFirstFatherNode(items);
    const nodeId = fatherNode?.dbPropierties?.__EDT_UUID__;
    const hierarchy = fatherNode?.dbPropierties?.hierarchy || this.getFirstHierarchy(items) || this.savedDocument.hierarchy;

    if (!nodeId || !hierarchy) return null;

    return { nodeId, hierarchy };
  }

  private getFirstFatherNode(items: AbstractShape[]): AbstractShape | null {
    for (const item of items) {
      if (item.dbPropierties?.__EDT_UUID__) return item;
    }

    for (const item of items) {
      const child = this.getFirstFatherNode(item.children);
      if (child) return child;
    }

    return null;
  }

  private getFirstHierarchy(items: AbstractShape[]): string {
    for (const item of items) {
      const hierarchy = item.dbPropierties?.hierarchy;
      if (hierarchy) return hierarchy;

      const childHierarchy = this.getFirstHierarchy(item.children);
      if (childHierarchy) return childHierarchy;
    }

    return "";
  }

  onExportData(): Observable<Exporter>{
    const pendingViewCyphers = this.updateContainers();
    const exportedDocument = this.importDocument;

    if (!pendingViewCyphers.length) {
      return of(new Exporter(exportedDocument, this.viewFromServer));
    }

    const cypherRequests = pendingViewCyphers.map((pendingViewCypher: PendingViewCypher) => {
      return this.beNeo4jService.getCypherById(
        pendingViewCypher.nodeId,
        pendingViewCypher.hierarchy,
        true,
        this.savedDocument.connection.dataService.string_connection
      ).pipe(
        rxMap((response: any) => {
          pendingViewCypher.view.sentence = response.result || "";
          return pendingViewCypher.view;
        })
      );
    });

    return forkJoin(cypherRequests).pipe(
      rxMap(() => new Exporter(exportedDocument, this.viewFromServer))
    );
  }

  endStartSentence = () => {
    this.findHyperFlows(() => {
      setTimeout(() => {
        this.isLoading = false;
        this.draw();
      }, 0);
    });
  }

  changeAllNodesTheme(ref: AbstractShape, theme: CanvasColorTheme){
    let filtered = getAllShapesWithLabels(ref.dbData.labels, this.canvasItems);
    for (const item of filtered) {
      item.setTheme(theme);
    }
    this.draw();
  }

  changeAllNodesShape(ref: AbstractShape, shape: Shape){
    let filtered = getAllShapesWithLabels(ref.dbData.labels, this.canvasItems);
    for (const item of filtered) {
      item.setShape(shape);
    }
    this.draw();
  }

  changeAllNodesPropiertyTheme(ref: AbstractShape, theme: CanvasColorTheme){
    let filtered = getAllShapesWithPropierty(ref.getGroup(), this.canvasItems);
    for (const item of filtered) {
      item.setTheme(theme);
    }
    this.draw();
  }

  changeAllNodesPropiertyShape(ref: AbstractShape, shape: Shape){
    let filtered = getAllShapesWithPropierty(ref.getGroup(), this.canvasItems);
    for (const item of filtered) {
      item.setShape(shape);
    }
    this.draw();
  }

  deleteRelationship(connection: Connection){
    this.connections = this.connections.filter((conn)=>connection.id != conn.id);
    this.updateContainers();
    this.draw();
  }

  deleteContainer(item: AbstractShape){
    let parent = item.parent;
    if(parent){
      parent.removeChild(item);
    }else{
      this.canvasItems = this.canvasItems.filter((i)=>i.id !== item.id);
    }
    this.connections = this.connections.filter((conn)=>{
      return item.id !== conn.iFrom.id && item.id !== conn.iTo!.id;
    });
    this.updateContainers();
    this.draw();
  }

  getConnectionJson() {
    return this.savedDocument;
  }

  getCurrentFocus() {
    return this.currentFocus;
  }

  getCurrentRelation() {
    return this.currentConnectionFocus;
  }

  getDBLabels() {
    return this.savedDocument.connection.DBlabels;
  }

  getDBRelations() {
    return this.savedDocument.connection.DBrelations;
  }

  getDataReadyFocus() {
    return this.dataReady;
  }

  getHierarchy() {
    return this.savedDocument.connection.hierarchy;
  }
  getCurrentSentence() {
    let sentences = this.viewCanvas.map((view: ViewsCanvas) => view.sentence);
    let docSentences = this.savedDocument.containers.map((view: IView) => view.sentence);
    return docSentences.length ? docSentences : sentences;
  }

  getSchemaTree() {
    return this.savedDocument.connection.schemaTree;
  }

  getViewFromServer() {
    return this.viewFromServer;
  }

  setViewFromServer(view: ViewFromServer) {
    if(!view)return;
    this.viewFromServer = view;
    if (view.hierarchy) this.savedDocument.hierarchy = view.hierarchy;
    if (view.model_UUID) this.savedDocument.model_UUID = view.model_UUID;
  }

  clearAllFocus(){
    clearConnectionFocus(this.connections);
    clearFocus(this.canvasItems);
    this.draw();
  }

  IsViewEditable(){
    return this._isViewEditable;
  }

  startCallback = ()=>{
    this.isLoading =  true;
  }
  endCallback = ()=>{
    this.isLoading =  false;
  }

  abstract getCameraState(): CameraState | null;
  abstract preserveCameraState(cameraState: CameraState | null): void;
  abstract draw(): void;
  abstract updateScreen(): void;
  abstract takeChildScreenshot(
    itemOrId: AbstractShape | string,
    options?: ScreenshotOptions): Promise<void>;
  protected abstract afterFetchingContent(): void;
  protected abstract errorReporting(title: string, message: string): void;
}

export interface ScreenshotOptions {
  fileName?: string;
  scale?: number;
  padding?: number;
  backgroundColor?: string | null;
  limitTo32Megapixels?: boolean;
}

export interface CameraState {
  offsetX: number;
  offsetY: number;
  scale: number;
}
